import { useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { IconArrowsExchange } from '@tabler/icons-react';
import { designCatalog, designVariants } from './catalog';
import { useDesignVariant } from './DesignVariantProvider';
import { useDesignWorkspace } from './DesignWorkspaceProvider';
import { useDesignStyles } from './styles';

export const DesignChooser = ({ compact = false }: { compact?: boolean }) => {
  const workspace = useDesignWorkspace();
  const [open, setOpen] = useState(false);
  const { variant, selectVariant } = useDesignVariant();
  const { classes } = useDesignStyles();
  return (
    <>
      <Button
        size="small"
        color="inherit"
        aria-label="Choose interface design"
        startIcon={compact ? undefined : <IconArrowsExchange size={17} />}
        onClick={() => setOpen(true)}
        sx={{ minWidth: 0, whiteSpace: 'nowrap' }}>
        {compact ? (
          <IconArrowsExchange size={17} />
        ) : variant === 'main' ? (
          'Designs'
        ) : (
          variant.toUpperCase()
        )}
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="design-chooser-title">
        <DialogTitle id="design-chooser-title">Choose your experience</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            All workspaces use your current company and permissions. Missions execute published
            actions; Ask Callevate requires confirmation before changes. Switching design keeps this
            page open.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {designVariants.map((id) => (
              <ButtonBase
                key={id}
                className={classes.choice}
                aria-pressed={id === variant}
                onClick={() => {
                  workspace.changeDesign(() => selectVariant(id));
                  setOpen(false);
                }}>
                <Box className={classes.miniScene} aria-hidden>
                  <Box
                    sx={{
                      width: id === 'v4' || id === 'v5' ? 20 : 44,
                      bgcolor: id === 'main' || id === 'v1' ? 'surface.raised' : 'background.paper',
                      borderInlineEnd: 1,
                      borderColor: 'divider',
                      p: 0.6,
                    }}>
                    {[0, 1, 2].map((n) => (
                      <Box
                        key={n}
                        sx={{
                          height: 4,
                          bgcolor: n === 0 ? 'primary.main' : 'divider',
                          mb: 0.7,
                          borderRadius: 1,
                        }}
                      />
                    ))}
                  </Box>
                  <Stack
                    direction={id === 'v2' ? 'row' : 'column'}
                    gap={0.5}
                    sx={{ flex: 1, p: 1 }}>
                    {[0, 1, 2].map((n) => (
                      <Box
                        key={n}
                        sx={{
                          flex: 1,
                          bgcolor: 'background.paper',
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
                <Stack direction="row" gap={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={600}>
                    {designCatalog[id].title}
                  </Typography>
                  {id === 'main' && <Chip label="Default" size="small" />}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {designCatalog[id].description}
                </Typography>
              </ButtonBase>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Selection is remembered in this browser tab for this user and company. A fresh session
            starts with Main.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Keep current design</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
